import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import usePlugins from '../../hooks/usePlugins';

interface DeletePluginModalProps {
  pluginId: string;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

export const DeletePluginModal = ({ pluginId, onClose, onDeleted }: DeletePluginModalProps) => {
  const { t } = useTranslation();
  const { plugins, deletePlugin, deleteStatus } = usePlugins();
  const isDeleting = deleteStatus === 'pending';
  const plugin = plugins.find((p) => p.plugin_id === pluginId);

  const handleDelete = useCallback(() => {
    deletePlugin(pluginId, {
      onSuccess: () => {
        onDeleted(pluginId);
        onClose();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  }, [pluginId, deletePlugin, onDeleted, onClose]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('plugins.delete_title')}</DialogTitle>
          <DialogDescription>{t('plugins.delete_warning', { 0: plugin?.name })}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost-neutral" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete} isLoading={isDeleting}>
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
